import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { generateSearchURL } from "@mirage/search/hooks/useQueryParams";
import type { SearchOptions } from "@mirage/service-search/service";
import { TypeaheadQueryAtom } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CategorySearchAccordion } from "./CategorySearchAccordion";
import styles from "./CategorySearchAccordionWrapper.module.css";
import { useCategoriesConfig } from "./useCategoriesConfig";

type Props = {
  query: string;
  header?: React.ReactNode;
  hasResults?: () => void;
  shouldSkipCache?: boolean;
};

export function CategorySearchAccordionWrapper({
  query,
  header,
  hasResults,
  shouldSkipCache,
}: Props) {
  const navigate = useNavigate();
  const categoriesConfig = useCategoriesConfig();
  const [isShown, setIsShown] = useState(false);

  const setTypeaheadQuery = useSetAtom(TypeaheadQueryAtom);
  const { searchAttemptSessionManager } = useMirageAnalyticsContext();
  const handleSearch = (
    query: string,
    filters?: SearchFilter[] | undefined,
    options?: Partial<SearchOptions>,
  ) => {
    searchAttemptSessionManager.updateProperties({
      searchSurface: "result_page",
    });
    setTypeaheadQuery(query);
    navigate(generateSearchURL(query, filters, options));
  };

  const handleFilterChange = useCallback(
    (nextFilters: SearchFilter[]) => {
      if (query) {
        handleSearch(query, nextFilters);
      }
    },
    [query],
  );

  const resultsFound = () => {
    setIsShown(true);
    hasResults?.();
  };

  return (
    <div className={isShown ? styles.shown : styles.hidden}>
      {header}
      <CategorySearchAccordion
        categories={categoriesConfig}
        seeAllForCategory={handleFilterChange}
        hasResults={resultsFound}
        query={query}
        shouldSkipCache={shouldSkipCache}
      />
    </div>
  );
}
